"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.GFReducePositionProcessor = exports.checkInfoReason = exports.calcPercentBasedOnNum = void 0;
const TradeAPILib_1 = require("TradeAPILib");
const trade_utils_lib_1 = require("trade_utils_lib");
const interfaces_1 = require("./interfaces");
const react_1 = require("react");
const apis_1 = require("./apis");
const rxjs_1 = require("rxjs");
const operators_1 = require("rxjs/operators");
const useReducePositionCheckInfo_1 = require("./useReducePositionCheckInfo");
const lodash_1 = require("lodash");
function calcDecimal(num, decimalPlaces) {
    return new trade_utils_lib_1.SafeDecimal(num).div(Math.pow(10, decimalPlaces)).toNumber();
}
function calcMinOrderPrecision(perVolume, symbolInfo) {
    var _a;
    const basePrecision = (_a = symbolInfo === null || symbolInfo === void 0 ? void 0 : symbolInfo.amount) !== null && _a !== void 0 ? _a : new trade_utils_lib_1.SafeDecimal(perVolume).decimalPlaces();
    return calcDecimal(1, basePrecision);
}
// Reduce Number To Percent 公式:
// 最大可减仓份数等于 = 单网格买卖量 / 下单精度(即交易对amount的精度)
// 可减仓比例的百分比 = 减仓份数(reduceNum) / 最大可减仓份数 * 100
function calcPercentBasedOnNum(perVolume, reduceNum, symbolInfo) {
    const base = new trade_utils_lib_1.SafeDecimal(perVolume).div(calcMinOrderPrecision(perVolume, symbolInfo));
    return new trade_utils_lib_1.SafeDecimal(reduceNum).div(base).mul(100).toNumber();
}
exports.calcPercentBasedOnNum = calcPercentBasedOnNum;
// 获取不允许调整参数的原因
function checkInfoReason({ reason, $st }) {
    switch (reason) {
        case interfaces_1.ReducePositionCheckFailedReason.LOSS_GTE_INVESTMENT: {
            return $st("gf_reduce_position_loss_gte_investment_error");
        }
        case interfaces_1.ReducePositionCheckFailedReason.POSITION_TOO_SMALL: {
            return $st("gf_reduce_position_position_too_small_error");
        }
        case interfaces_1.ReducePositionCheckFailedReason.REDUCE_TOO_MUCH: {
            return $st("gf_reduce_position_reduce_too_much_error");
        }
        default: {
            return "";
        }
    }
}
exports.checkInfoReason = checkInfoReason;
const GFReducePositionProcessor = (props) => {
    const { curPrice, symbolInfo, exchangeOrder, $st, reduce_num_percent, orderType } = props;
    const [minPercent, setMinPercent] = (0, react_1.useState)(0);
    const [maxPercent, setMaxPercent] = (0, react_1.useState)(0);
    const order = exchangeOrder.exchangeGridV4FuturesOrder;
    const { per_volume } = order;
    const reduce_num = (0, react_1.useMemo)(() => {
        if ((0, lodash_1.isUndefined)(reduce_num_percent)) {
            return 0;
        }
        return new trade_utils_lib_1.SafeDecimal(per_volume)
            .div(calcMinOrderPrecision(per_volume, symbolInfo))
            .mul(new trade_utils_lib_1.SafeDecimal(reduce_num_percent))
            .div(100)
            .toDecimalPlaces(0, trade_utils_lib_1.SafeDecimal.ROUND_CEIL)
            .toNumber();
    }, [per_volume, reduce_num_percent, symbolInfo]);
    const { checkInfo, queryCheckInfo, queryCheckInfoWithParam, loading: checkLoading, } = (0, useReducePositionCheckInfo_1.useReducePositionCheckInfo)({
        reduce_num,
        open_price: curPrice,
        orderId: exchangeOrder.id,
    }, orderType, reduce_num_percent);
    (0, react_1.useEffect)(() => {
        if (!checkInfo || !checkInfo.check_result || !checkInfo.max_reduce_num) {
            return;
        }
        const min = new trade_utils_lib_1.SafeDecimal(calcMinOrderPrecision(per_volume, symbolInfo)).div(new trade_utils_lib_1.SafeDecimal(per_volume)).mul(100);
        const max = calcPercentBasedOnNum(per_volume, checkInfo.max_reduce_num, symbolInfo);
        setMinPercent(Math.max(min.toNumber(), 1));
        setMaxPercent(max);
    }, [checkInfo, per_volume, symbolInfo]);
    const checkInputError = (0, react_1.useMemo)(() => {
        if (!checkInfo || new trade_utils_lib_1.SafeDecimal(maxPercent).eq(0)) {
            return undefined;
        }
        if (new trade_utils_lib_1.SafeDecimal(reduce_num_percent).lt(1)) {
            return $st("gf_reduce_position_higher_than_min", { min: 1 });
        }
        if (new trade_utils_lib_1.SafeDecimal(reduce_num_percent).gt(new trade_utils_lib_1.SafeDecimal(maxPercent))) {
            return $st("gf_reduce_position_lower_than_max", { max: new trade_utils_lib_1.SafeDecimal(maxPercent).toFixed(2, trade_utils_lib_1.SafeDecimal.ROUND_DOWN) });
        }
        return undefined;
    }, [$st, checkInfo, maxPercent, reduce_num_percent]);
    const submit = (0, react_1.useCallback)(() => {
        if (!reduce_num) {
            return (0, rxjs_1.throwError)($st("gf_reduce_position_empty_error"));
        }
        if (!curPrice) {
            return (0, rxjs_1.throwError)($st("trade_query_ticker_error"));
        }
        return queryCheckInfo().pipe((0, operators_1.mergeMap)((info) => {
            if (info.check_result) {
                return (0, apis_1.reducePositionAPI)({
                    reduce_num: reduce_num,
                    orderId: exchangeOrder.id,
                    open_price: curPrice,
                }, orderType);
            }
            const r = checkInfoReason({ reason: info === null || info === void 0 ? void 0 : info.reason, $st });
            if (r) {
                // 减仓check时肯能出现的错误
                return (0, rxjs_1.throwError)(r);
            }
            return (0, rxjs_1.throwError)({ code: -1, message: "check failed", data: info });
        }));
    }, [$st, curPrice, exchangeOrder.id, orderType, queryCheckInfo, reduce_num]);
    const submitWithParam = (0, react_1.useCallback)((r_num, current_price) => {
        if (!r_num) {
            return (0, rxjs_1.throwError)($st("gf_reduce_position_empty_error"));
        }
        if (!current_price) {
            return (0, rxjs_1.throwError)($st("trade_query_ticker_error"));
        }
        return queryCheckInfoWithParam(r_num, current_price).pipe((0, operators_1.mergeMap)((info) => {
            if (info.check_result) {
                return (0, apis_1.reducePositionAPI)({
                    reduce_num: r_num,
                    orderId: exchangeOrder.id,
                    open_price: current_price,
                }, orderType);
            }
            const r = checkInfoReason({ reason: info === null || info === void 0 ? void 0 : info.reason, $st });
            if (r) {
                // 减仓check时肯能出现的错误
                return (0, rxjs_1.throwError)(r);
            }
            return (0, rxjs_1.throwError)({ code: -1, message: "check failed", data: info });
        }));
    }, [$st, exchangeOrder.id, orderType, queryCheckInfoWithParam]);
    const stopLossInfo = (0, react_1.useMemo)(() => {
        if (!checkInfo || !checkInfo.check_result) {
            return undefined;
        }
        const order = exchangeOrder.exchangeGridV4FuturesOrder;
        if ((order === null || order === void 0 ? void 0 : order.loss_stop_type) !== TradeAPILib_1.GridStopType.profit_amount) {
            return undefined;
        }
        if (new trade_utils_lib_1.SafeDecimal(order === null || order === void 0 ? void 0 : order.stop).lte(0)) {
            return undefined;
        }
        const _totalProfit = new trade_utils_lib_1.SafeDecimal(order === null || order === void 0 ? void 0 : order.totalProfit);
        const _totalReduced = new trade_utils_lib_1.SafeDecimal(checkInfo === null || checkInfo === void 0 ? void 0 : checkInfo.reduce_gridprofit).add(new trade_utils_lib_1.SafeDecimal(checkInfo === null || checkInfo === void 0 ? void 0 : checkInfo.reduce_pnl));
        let res = {
            profitOrigin: _totalProfit.toFixed(),
            totalReducedProfit: _totalReduced.toFixed(),
            profitAfterReduce: _totalProfit.minus(_totalReduced).toFixed(),
        };
        return res;
    }, [checkInfo, exchangeOrder.exchangeGridV4FuturesOrder]);
    const stopLossRisk = (0, react_1.useMemo)(() => {
        if (!stopLossInfo) {
            return false;
        }
        return new trade_utils_lib_1.SafeDecimal(stopLossInfo.profitAfterReduce).lt(new trade_utils_lib_1.SafeDecimal(order === null || order === void 0 ? void 0 : order.stop));
    }, [order === null || order === void 0 ? void 0 : order.stop, stopLossInfo]);
    return {
        checkInfo,
        checkLoading,
        submit,
        submitWithParam,
        minPercent,
        maxPercent,
        reduce_num,
        stopLossInfo,
        stopLossRisk,
        checkInputError,
        checkInfoError: checkInfoReason({ reason: checkInfo === null || checkInfo === void 0 ? void 0 : checkInfo.reason, $st }),
    };
};
exports.GFReducePositionProcessor = GFReducePositionProcessor;
